import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseSecurityOwnership {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }


    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }



    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
        this.attachStyles();
        await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');

        this.emit('ready', {});
        this.config.onReady?.();
        cancelSelectEvent(this.shadowRoot);
    }

    private createWidget(): void {

         let themeClass = 'defaultDarkMode';
let dataTheme = 'default-dark';

if (this.config.theme === 'white') {
  themeClass = 'whiteMode';
  dataTheme = 'white';
} else if (this.config.theme === 'black') {
  themeClass = 'blackMode';
  dataTheme = 'black';
} else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()}Security Ownership</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                                          <div class="trader-sm-card" id="selected-symbol">
                                          ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                                          </div>
                                          <span id="symbol-dropdown-placeholder"></span>
                                          </div>`}
            <span></span>
          </div>
          <div class="chartDv" id="eventsContainer"></div>
        </div>
      </div>
    `;

        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }

        this.setupChart(this.config.symbol!);
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async setupChart(symbol: string): Promise<void> {
        try {
            const response = await apiGet({
                method: "POST",
                url: `${APIURL}/v1/company/web_premium_table/security_ownership?symbol=${encodeURIComponent(symbol)}`,
                body: { symbol },
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            this.currentData = await response.json();

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }



    private filterByCountry(): void {
        const rows = Array.isArray(this.currentData?.data) ? this.currentData.data : [];
        this.renderShareStructure(rows);
    }


    private renderShareStructure(stats: any[]): void {
        const container = this.shadowRoot.querySelector('#eventsContainer');
const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

if (!container || !chartDv) return;

const hasData = Array.isArray(stats) && stats.length > 0;

// ONLY set testing id when data exists
if (hasData) {
  chartDv.setAttribute('data-testing-id', 'Security Ownership');
} else {
  chartDv.removeAttribute('data-testing-id');
}
        if (!stats.length) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const formatNumber = (val: any, maxDigits: number = 0): string => {
            if (val === null || val === undefined) return "-";
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: maxDigits
            });
        };

        const formatPercent = (val: any): string => {
            if (val === null || val === undefined) return "-";
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            return `${num.toFixed(2)}%`;
        };

        const formatCurrency = (val: any): string => {
            if (val === null || val === undefined) return "-";
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };

        const formatSignedNumber = (val: any, maxDigits: number = 0): string => {
            if (val === null || val === undefined) return "-";
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            const sign = num > 0 ? "+" : "";
            return `${sign}${formatNumber(num, maxDigits)}`;
        };

        const formatSignedCurrency = (val: any): string => {
            if (val === null || val === undefined) return "-";
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            const sign = num > 0 ? "+" : "";
            return `${sign}$${Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };

        const formatSignedPercent = (val: any): string => {
            if (val === null || val === undefined) return "-";
            const num = typeof val === 'number' ? val : Number(val);
            if (Number.isNaN(num)) return String(val);
            const sign = num > 0 ? "+" : "";
            return `${sign}${num.toFixed(2)}%`;
        };

        let html = `
      <div class="eventsMain customGrid shareStructure--" style="--grid-template-columns: minmax(220px,2.2fr) minmax(90px,0.9fr) minmax(110px,1fr) minmax(110px,1fr) minmax(110px,1fr) minmax(125px,1fr) minmax(125px,1fr) minmax(130px,1.1fr) minmax(130px,1.1fr) minmax(110px,0.95fr) minmax(110px,0.95fr) minmax(130px,1.1fr); --grid-column-gap: 14px; min-width: 1700px;">
        <div class="item header">
          <p>Name</p>
          <p>Form Type</p>
          <p>Effective Date</p>
          <p>File Date</p>
          <p>Shares</p>
          <p>Share Change</p>
          <p>Share % Change</p>
          <p>Value</p>
          <p>Value Change</p>
          <p>Value % Change</p>
          <p>Ownership %</p>
          <p>Ownership % Change</p>
        </div>
    `;

        for (const row of stats) {
            const shareChange = Number(row.sharesChange ?? 0);
            const valueChange = Number(row.valueChange ?? 0);
            const valueChangePercent = Number(row.valuePercentChange ?? 0);
            const ownershipPercentChange = Number(row.ownershipPercentChange ?? 0);

            html += `
        <div class="item body">
          <p title="${row.name ?? '-'}">${row.name ?? "-"}</p>
          <p>${row.formType ?? "-"}</p>
          <p>${row.effectiveDate ?? "-"}</p>
          <p>${row.fileDate ?? "-"}</p>
          <p>${formatNumber(row.shares)}</p>
          <p class="stat left ${shareChange < 0 ? "down" : "up"}"><span>${formatSignedNumber(row.sharesChange)}</span></p>
          <p class="stat left ${shareChange < 0 ? "down" : "up"}"><span>${formatSignedPercent(row.sharesPercentChange)}</span></p>
          <p>${formatCurrency(row.value)}</p>
          <p class="stat left ${valueChange < 0 ? "down" : "up"}"><span>${formatSignedCurrency(row.valueChange)}</span></p>
          <p class="stat left ${valueChangePercent < 0 ? "down" : "up"}"><span>${formatSignedPercent(row.valuePercentChange)}</span></p>
          <p>${formatPercent(row.ownershipPercent)}</p>
          <p class="stat ${ownershipPercentChange < 0 ? "down" : "up"}"><span>${formatSignedPercent(row.ownershipPercentChange)}</span></p>
        </div>`;
        }

        html += `</div>`;
        container.innerHTML = html;
    }

    private updateDisplay(): void {
        if (!this.currentData) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.filterByCountry();
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
  if (!this.shadowRoot) return;

  this.config.theme = toggleWidgetTheme(
    this.shadowRoot,
    theme
  ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

  this.updateDisplay();
}

    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseSecurityOwnership: typeof TraderverseSecurityOwnership; } }
if (typeof window !== 'undefined') { window.TraderverseSecurityOwnership = TraderverseSecurityOwnership; }
